import { getAmplitudeInstance } from '../amplitudeHelpers';
import ExploreViewType from '../enums/ExploreViewType';
import FilterName from '../enums/FilterName';
import LocationType from '../enums/LocationType';
import MapBaseLayer from '../enums/MapBaseLayer';
import MapOverlay from '../enums/MapOverlay';

type logExploreViewSwitchedParams = {
  bearing?: number;
  collections_applied?: string[];
  collections_available?: string[];
  coordinates?: number[];
  explore_view_type: ExploreViewType;
  filters_applied?: FilterName[];
  location_id?: number;
  location_type?: LocationType;
  map_base_layer?: MapBaseLayer;
  map_overlays?: MapOverlay[];
  method: string;
  new_state: ExploreViewType;
  num_pois?: number;
  num_results_returned?: number;
  park_id?: number;
  pitch?: number;
  previous_state: ExploreViewType;
  zoom_level?: number;
};

/**
 * Track when users switch between list-view and map-view. Trigger ExploreViewSwitched when the user clicks on the map icon or fully collapses the list-view (mobile).
 * @param params - Attributes to log with the amplitude event
 * @param params.bearing - Numeric degrees indicating the bearing.
 * @param params.collections_applied - The name for the collections that were set when an action took place.
 * @param params.collections_available - List of strings containing the collections available to the user.
 * @param params.coordinates - Float array of center
 * @param params.explore_view_type - Describes the type of explore view the user was in when the action took place (e.g., ‘list’,  ‘map’ or ‘split’)
 * @param params.filters_applied - List of strings containing the filters applied by the user
 * @param params.location_id - Unique identifier of the location that was set at the time the action took place
 * @param params.location_type - Type of location set (e.g.  ‘city’, ‘park’, ‘map area’, the pois  etc.) Note: All locations correspond to pois, with the exception of map area. The ‘map area’ location type is set when the user freely pans in the open map mode and the ‘map area’ term shows up in the search box.
 * @param params.map_base_layer - Name of tile base layer currently selected
 * @param params.map_overlays - The overlays currently applied to the map.
 * @param params.method - Method of switching views (swipe or click)
 * @param params.new_state - State the user switched to when changing explore view
 * @param params.num_pois - The number of POI pins in the view
 * @param params.num_results_returned - Total number of trails returned
 * @param params.park_id - Unique identifier of the park that launched parks mode in explore view.
 * @param params.pitch - Numeric value indicating the tilt.
 * @param params.previous_state - State the user was previously in before changing explore view
 * @param params.zoom_level -  Numeric value indicating the zoom level.
 */
const logExploreViewSwitched = (
  {
    bearing,
    collections_applied,
    collections_available,
    coordinates,
    explore_view_type,
    filters_applied,
    location_id,
    location_type,
    map_base_layer,
    map_overlays,
    method,
    new_state,
    num_pois,
    num_results_returned,
    park_id,
    pitch,
    previous_state,
    zoom_level
  }: logExploreViewSwitchedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'explore view switched',
    {
      bearing,
      'collections applied': collections_applied,
      'collections available': collections_available,
      coordinates,
      'explore view type': explore_view_type,
      'filters applied': filters_applied,
      'location id': location_id,
      'location type': location_type,
      'map base layer': map_base_layer,
      'map overlays': map_overlays,
      method,
      'new state': new_state,
      'num pois': num_pois,
      'num results returned': num_results_returned,
      'park id': park_id,
      pitch,
      'previous state': previous_state,
      'zoom level': zoom_level
    },
    callback
  );
};

export default logExploreViewSwitched;
